import {
  AppBar,
  Button,
  buttonClasses,
  Checkbox,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Menu,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { CloseCircle, DirectionDown, Magnifier } from 'asset';
import {
  DeleteDialog,
  FilterMenu,
  HeadTable,
  Layout,
  PopupDetailReport,
  TableCaption,
} from 'components';
import DeleteAllDialog from 'components/common/DeleteAllDialog';
import { ButtonDelete, ButtonDetail } from 'components/common/table';
import { drawerWidth } from 'config';
import { getSortItem, hasPermission, NotifyService } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import {
  DELETE_DIALOG_TYPE,
  FilterOption,
  FilterValue,
  GET_REPORT_LIST_PARAMS,
  REPORT_TYPE,
  reportStatusColor,
  TYPE_POPUP,
} from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import { deleteManyReportApi } from 'services';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setReportListParams } from 'store/reducers/reports';
import { getReportListRequest } from 'store/reducers/reports/actionType';
import { headCells } from './data';

const filterOptions: FilterOption[] = [];

const Reports = () => {
  const theme = useTheme();
  const { formatDateTime } = useDateTime();
  const dispatch = useAppDispatch();
  const { report_list } = useAppSelector(
    (state) => state.reportsReducer.reports
  );
  const { params } = report_list;
  const [anchorElAction, setAnchorElAction] = useState<null | HTMLElement>(
    null
  );
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openDetailPopup, setOpenDetailPopup] = useState(false);
  const [itemAction, setItemAction] = useState<REPORT_TYPE>();
  const [openDeleteAll, setOpenDeleteAll] = useState<boolean>(false);

  const onSetListParams = (params: GET_REPORT_LIST_PARAMS | null) => {
    dispatch(setReportListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getReportListRequest(params));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.keyword ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpenDelete = (event: any, data: REPORT_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenDeletePopup(true);
  };

  const setOpenDetail = (event: any, data: REPORT_TYPE) => {
    event.stopPropagation();
    setItemAction(data);
    setOpenDetailPopup(true);
  };

  const handleOpenBulkAction = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAction(event.currentTarget);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = report_list.records.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_: any, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const { items } = usePagination({
    count: report_list.metadata.total_pages,
    page: report_list.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.keyword = keyword;
    else delete _params.keyword;
    onSetListParams(_params);
  }, 250);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemAction(undefined);
        break;
      case TYPE_POPUP.DETAIL:
        setOpenDetailPopup(false);
        setItemAction(undefined);
        break;
      case TYPE_POPUP.DELETE_ALL:
        setOpenDeleteAll(false);
        break;
    }
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getReportListRequest(params));
  };
  const deleteManyPayment = async () => {
    const paymentIds = selected as string[];
    await deleteManyReportApi(paymentIds)
      .then((r) => {
        onReloadList();
        setSelected([]);
        onClosePopup(TYPE_POPUP.DELETE_ALL);
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {});
  };

  const filterValue: FilterValue = useMemo(() => {
    return {};
  }, []);

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };

    onSetListParams({
      ..._params,
      ...value,
      page: 1,
    });
  };

  const sortItem = useMemo(() => {
    return getSortItem(report_list.params?.sort);
  }, [report_list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof REPORT_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Reports
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
            />
          </Grid>
          <Grid display="flex" gap="8px">
            {hasPermission(['superadmin', 'staff']) && (
              <>
                <Button
                  sx={{
                    background: '#fff',
                    border: `1px solid #226FE3`,
                    borderRadius: '3px',
                    boxShadow: 'none',

                    color: '#226FE3',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '100%',
                    textTransform: 'none',

                    p: '6px 16px',
                    height: '30px',
                    alignItems: 'flex-end',
                    ':hover': {
                      background: 'rgba(34, 111, 227, 0.08)',
                      border: `1px solid ${theme.palette.secondary.main}`,
                      borderRadius: '3px',
                      boxShadow: 'none',
                    },
                    [`& .${buttonClasses.endIcon}`]: {
                      mb: '-3px',
                      ml: '3px',
                    },
                  }}
                  variant="contained"
                  endIcon={<DirectionDown />}
                  onClick={handleOpenBulkAction}
                >
                  Bulk Action
                </Button>
                <Menu
                  sx={{
                    mt: '45px',
                    '& .MuiList-padding': {
                      pt: '0px !important',
                      pb: '0px !important',
                    },
                  }}
                  id="menu-appbar"
                  anchorEl={anchorElAction}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElAction)}
                  onClose={() => setAnchorElAction(null)}
                >
                  <Stack
                    sx={{
                      width: '319px',
                      p: '14px 22px 25px 23px',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      ':hover': {
                        backgroundColor: 'white.light',
                      },
                    }}
                  >
                    <Stack spacing="10px">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '100%',
                        }}
                      >
                        Bulk Actions
                      </Typography>
                      <Button
                        startIcon={
                          <CloseCircle
                            width={15}
                            heigh={15}
                            color={theme.palette.secondary.main}
                          />
                        }
                        variant="outlined"
                        color="secondary"
                        sx={{
                          p: 0,
                          fontWeight: 600,
                          fontSize: '15px',
                          lineHeight: 1,
                          width: '230px',
                          height: '32px',
                          borderRadius: '10px',
                          '& .MuiButton-startIcon': {
                            mr: '5px',
                          },
                        }}
                        onClick={() => setOpenDeleteAll(true)}
                      >
                        Delete Selected Reports
                      </Button>
                    </Stack>
                  </Stack>
                </Menu>
                {/* <Button
              sx={{
                width: '131px',
                height: 30,
                fontWeight: 600,
                fontSize: '13px',
                border: `1px solid ${theme.palette.secondary.main}`,
                p: '0px',
              }}
              variant="outlined"
              color="secondary"
              endIcon={<DocumentDownload />}
            >
              Export to CSV
            </Button> */}
              </>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<REPORT_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={report_list.records.length}
              headCells={headCells}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {report_list.records.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                      }}
                    >
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={(e) => handleClick(e, row.id)}
                        inputProps={{
                          'aria-labelledby': labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#1F64CC',
                      }}
                    >
                      {row.target?.first_name}&nbsp;{row.target?.last_name}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row.ReportReason?.reason}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#1F64CC',
                      }}
                    >
                      {row.actor?.first_name}&nbsp;{row.actor?.last_name}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                        textTransform: 'capitalize',
                        color: reportStatusColor[row.status || ''] || '#B6B6B6',
                      }}
                    >
                      {row.status}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {formatDateTime(row.createdAt)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <ButtonDetail onClick={(e) => setOpenDetail(e, row)} />
                        {hasPermission(['superadmin', 'staff']) && (
                          <ButtonDelete
                            onClick={(e) => setOpenDelete(e, row)}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={report_list.metadata}
          items={items}
        />
      </Stack>

      {openDeletePopup && (
        <DeleteDialog
          name=""
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={itemAction?.id}
          onSuccess={onReloadList}
          type={DELETE_DIALOG_TYPE.REPORT}
        />
      )}
      {openDeleteAll && (
        <DeleteAllDialog
          open={openDeleteAll}
          onClose={() => setOpenDeleteAll(false)}
          onClickDelete={deleteManyPayment}
        />
      )}
      {openDetailPopup && (
        <PopupDetailReport
          open
          subject={itemAction?.id}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.DETAIL)}
        />
      )}
    </Layout>
  );
};

export default Reports;
